import type { ReactNode } from 'react';
import { Card, IconTile } from '@/components/ui';
import { useRaknaUpp } from '@/hooks/useRaknaUpp';
import { cn } from '@/lib/utils';

/**
 * Nyckeltalskortet på Insikter. Delas av ELvis-, Elvira- och
 * ledningsrapportfliken.
 *
 * `value = null` betyder att talet inte finns (t.ex. en månad innan ELvis
 * började logga) – då står ett tankstreck, aldrig en nolla som ljuger.
 * Ikonrutan är valfri: ledningsrapporten bär inga ikoner.
 */
export function StatCard({
  label,
  value,
  sub,
  icon,
  tone = 'neutral',
}: {
  label: string;
  value: number | null;
  sub: ReactNode;
  icon?: ReactNode;
  tone?: 'brand' | 'neutral' | 'success' | 'warning';
}) {
  // "Senaste 7 dagar" finns på båda flikarna – undertexten skiljer dem åt.
  const visat = useRaknaUpp(value ?? 0, `${label}|${typeof sub === 'string' ? sub : ''}`);
  return (
    <Card variant="glass" className="p-5 h-full">
      <div className="flex items-center gap-2 mb-3 min-h-7">
        {icon && <IconTile size="sm" tone={tone} icon={icon} />}
        {/* Utan ikon får etiketten bryta i stället för att kapas – rapportens etiketter är längre. */}
        <span className={cn('text-[11px] font-bold uppercase tracking-wider text-ink-500', icon ? 'truncate' : 'leading-snug')}>
          {label}
        </span>
      </div>
      <p className="text-display text-[26px] text-ink-900 leading-none tabular-nums">
        {value === null ? '–' : visat.toLocaleString('sv-SE')}
      </p>
      <p className="text-[11.5px] font-semibold text-ink-500 mt-2">{sub}</p>
    </Card>
  );
}
